import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Info } from "@phosphor-icons/react";

const RES_TONES = {
    high: "text-accent",
    medium: "text-foreground",
    low: "text-muted-foreground",
};

const DEFAULT_TOOLTIP =
    "Resonance estimates how strongly this narrative emotionally registers across conversations. It combines engagement, repetition, emotional intensity, and cross-platform recurrence. It is NOT a popularity metric — high resonance means people strongly identify with or emotionally respond to the idea, not that it is objectively true.";

/**
 * Editorial resonance display with qualitative label + numeric + explainer popover.
 *
 * Props:
 *   label       "high" | "medium" | "low"
 *   value       numeric score
 *   breakdown   { factors: [string, ...], signal_count, recurrence, platform_count, ... }
 *   compact     bool — tight single-line rendering
 *   inverseTheme bool — render light on dark backgrounds
 */
export default function ResonanceLabel({
    label = "low",
    value = 0,
    breakdown = null,
    compact = false,
    inverseTheme = false,
}) {
    const [open, setOpen] = useState(false);
    const tone = RES_TONES[label] || RES_TONES.low;
    const cap = label ? label.charAt(0).toUpperCase() + label.slice(1) : "—";
    const numeric = typeof value === "number" ? value.toLocaleString() : value;
    const baseTextColor = inverseTheme ? "text-white/90" : "";
    const mutedColor = inverseTheme ? "text-white/55" : "text-muted-foreground";
    const factors = breakdown?.factors || [];

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={`resonance-${label}`}
                    onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                    className={`inline-flex items-baseline gap-1.5 cursor-pointer group transition-opacity hover:opacity-80 ${baseTextColor}`}
                    title={DEFAULT_TOOLTIP}
                >
                    <span className={`font-mono text-[11px] tracking-wider ${compact ? "" : "font-semibold"} ${tone}`}>
                        {cap}
                    </span>
                    <span className={`font-mono tabular-nums text-[11px] ${inverseTheme ? "text-white/70" : "text-foreground/80"}`}>
                        · {numeric}
                    </span>
                    <Info size={10} className={`${mutedColor} group-hover:text-foreground transition-colors mt-[1px]`} />
                </button>
            </PopoverTrigger>
            <PopoverContent
                data-testid="resonance-popover"
                align="start"
                sideOffset={6}
                className="w-[320px] p-4 rounded-sm border border-border bg-popover text-popover-foreground"
            >
                <div className="flex items-baseline gap-2 mb-2">
                    <span className="overline text-[10px] tracking-[0.18em]">Resonance</span>
                    <span className={`font-display font-bold text-base ${RES_TONES[label] || ""}`}>{cap}</span>
                    <span className="font-mono tabular-nums text-[11px] text-muted-foreground">· {numeric}</span>
                </div>
                <p className="text-[12px] text-foreground/80 leading-relaxed mb-3">
                    A composite narrative signal — not a popularity metric.
                    High resonance means people strongly identify with or emotionally
                    respond to the idea, not that it is objectively true.
                </p>
                {factors.length > 0 && (
                    <>
                        <div className="overline text-[9px] tracking-[0.18em] mb-1.5">Why it scored {label}</div>
                        <ul className="space-y-1">
                            {factors.map((f, i) => (
                                <li key={i} className="text-[12px] text-foreground/85 pl-3 border-l border-border/70">
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                <p className="mt-3 pt-3 border-t border-border/60 text-[10px] text-muted-foreground leading-relaxed">
                    Current score is a transitional estimate (sum of likes/upvotes).
                    Future models will weight reply depth, emotional intensity, and
                    narrative persistence more richly.
                </p>
            </PopoverContent>
        </Popover>
    );
}
